import { Injectable, signal } from "@angular/core";

/**
 * 
 * BasicSettings are only for the following:
 * - inline
 * - crossline
 * - depth
 * - iteration
 */

interface SettingEntry {
  data: Record<string, any>;
  lastModified: number; // Timestamp for pruning
}

interface SettingsStore {
  [id: string]: SettingEntry;
}

@Injectable({ providedIn: "root" })
export class BasicSettingsService {
  private readonly STORAGE_KEY = "basicSettings";
  private readonly MAX_ENTRIES = 20;

  loadAll(): SettingsStore {
    const cached = localStorage.getItem(this.STORAGE_KEY);
    try {
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  }

  save(id: string, data: Record<string, any>): void {
    if (!id) return;

    const store = this.loadAll();

    // 1. Update or Add the entry with a new timestamp
    store[id] = {
      data: { ...(store[id]?.data || {}), ...data },
      lastModified: Date.now(),
    };

    // 2. Check if we exceeded the limit
    const keys = Object.keys(store);
    if (keys.length > this.MAX_ENTRIES) {
      this.pruneOldest(store, keys);
    }

    // 3. Persist back to localStorage
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(store));
  }

  private pruneOldest(store: SettingsStore, keys: string[]): void {
    // Sort keys by the lastModified timestamp (oldest first)
    const sortedKeys = keys.sort(
      (a, b) => store[a].lastModified - store[b].lastModified,
    );

    // Remove the oldest entries until we are back at the limit
    const entriesToRemove = sortedKeys.slice(
      0,
      sortedKeys.length - this.MAX_ENTRIES,
    );
    entriesToRemove.forEach((key) => delete store[key]);
  }

  private get(id: string): Record<string, any> {
    const store = this.loadAll();
    if (id in store) {
      return store[id]["data"] ?? {};
    }
    return {};
  }

  public readonly jobSettings = signal<{ [key: string]: any }>({});
  
  loadActiveJobSettings(id: string) {
    let loaded = this.get(id);
    this.jobSettings.set(loaded);
  }
}
